/**
 * Componente optimizado para mostrar avatares con caché inteligente.
 * Usa el hook useCachedAvatar para gestión de caché y precarga.
 * Implementa lazy loading y fallbacks para mejor UX.
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.userId - ID del usuario
 * @param {string} props.imageUrl - URL de la imagen del avatar
 * @param {Array} props.nearbyUsersIds - Lista de IDs de usuarios cercanos
 * @param {Object} props.style - Estilos adicionales
 * @param {boolean} props.showPlaceholder - Muestra placeholder durante carga
 * @param {number} props.customSize - Tamaño personalizado
 * @param {number} props.bradius - Border radius personalizado
 */

import React, { useState, useEffect } from 'react';
import { useCachedAvatar } from '../hooks/useCachedAvatar';
import { defaultImage } from '../constants/defaultImage';

export default function CachedAvatarImage({
  userId,
  imageUrl,
  nearbyUsersIds = [],
  style = {},
  showPlaceholder = true,
  customSize = 44,
  bradius = 8,
}) {
  const { source } = useCachedAvatar(userId, imageUrl, nearbyUsersIds);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  /**
   * Maneja la carga exitosa de la imagen
   */
  const handleLoad = () => {
    console.log(`[CachedAvatarImage] Imagen cargada: ${userId}`);
    setLoading(false);
    setError(false);
  };

  /**
   * Maneja el error de carga de la imagen
   */
  const handleError = () => {
    console.error(`[CachedAvatarImage] Error cargando imagen: ${userId}`);
    setLoading(false);
    setError(true);
  };

  /**
   * Efecto para resetear estado cuando cambia la imagen
   */
  useEffect(() => {
    setLoading(true);
    setError(false);
  }, [source]);

  /**
   * Renderiza el placeholder durante la carga
   */
  const renderPlaceholder = () => {
    if (!showPlaceholder) return null;

    return (
      <div 
        className="bg-neutral-800 absolute inset-0 flex items-center justify-center"
      >
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" 
             style={{ borderColor: '#DA0037', borderTopColor: 'transparent' }} />
      </div>
    );
  };

  /**
   * Renderiza el estado de error o cuando no hay imagen (usa imagen por defecto)
   */
  const renderErrorOrNoImage = () => {
    return (
      <img
        src={defaultImage}
        alt="Avatar por defecto"
        className="w-full h-full object-cover"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    );
  };

  /**
   * Estilos por defecto combinados con estilos personalizados
   */
  const defaultStyle = {
    width: customSize,
    height: customSize,
    borderRadius: bradius,
    overflow: 'hidden',
    position: 'relative',
    ...style,
  };

  return (
    <div style={defaultStyle} className='bg-neutral-800'>
      {!source ? (
        // No hay imagen - mostrar div de color morado
        renderErrorOrNoImage()
      ) : error ? (
        // Estado de error
        renderErrorOrNoImage()
      ) : (
        // Imagen normal
        <>
          <img
            src={source.uri}
            alt={`Avatar ${userId}`}
            className="w-full h-full object-cover"
            loading="lazy"
            onLoad={handleLoad}
            onError={handleError}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: loading ? 0 : 1,
              transition: 'opacity 0.3s ease',
            }}
          />
          
          {/* Placeholder durante carga */}
          {loading && renderPlaceholder()}
        </>
      )}
    </div>
  );
}
